import {
    ChatBubbleLeftRightIcon,
    EnvelopeIcon,
    PhoneArrowUpRightIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import { ActivityParentEntityId, PhoneNumberE164 } from "@sp-crm/core";
import { PhoneCall } from "components/telecom/phone-call";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton, primaryClasses } from "components/ui/primary-button";
import { SecondaryButton, secondaryClasses } from "components/ui/secondary-button";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EmailActionState } from "./marketing-plan-agenda";

export const contactPath = (contactId: string): string =>
    `/references/contacts/show/${contactId}`;

const provisionPath = "/settings/account/phone";

const responsiveWidth = "w-full sm:w-auto";
const iconClass = "mr-1.5 h-4 w-4 flex-shrink-0";

const ButtonLink: React.FC<{
    to: string;
    variant: "primary" | "secondary";
    children: React.ReactNode;
}> = ({ to, variant, children }) => (
    <Link
        to={to}
        className={`items-center ${responsiveWidth} ${
            variant === "primary" ? primaryClasses : secondaryClasses
        }`}>
        {children}
    </Link>
);

export const AgendaEmailAction: React.FC<{
    state: EmailActionState;
    contactId: string;
    onSendEmail: () => void;
}> = ({ state, contactId, onSendEmail }) => {
    if (state === "email-opted-out") {
        return (
            <span className="self-center text-sm italic text-gray-400">
                Opted out of email
            </span>
        );
    }
    if (state === "add-email") {
        return (
            <ButtonLink to={contactPath(contactId)} variant="secondary">
                <PlusIcon className={iconClass} />
                Add email
            </ButtonLink>
        );
    }
    return (
        <PrimaryButton
            onClick={onSendEmail}
            className={`items-center ${responsiveWidth}`}>
            <EnvelopeIcon className={iconClass} />
            Send email
        </PrimaryButton>
    );
};

export const AgendaAddContactPhone: React.FC<{ contactId: string }> = ({ contactId }) => (
    <ButtonLink to={contactPath(contactId)} variant="secondary">
        <PlusIcon className={iconClass} />
        Add phone number
    </ButtonLink>
);

export const AgendaProvisionPhone: React.FC<unknown> = () => (
    <ButtonLink to={provisionPath} variant="secondary">
        <PhoneArrowUpRightIcon className={iconClass} />
        Set up calling
    </ButtonLink>
);

export const AgendaCallAction: React.FC<{
    phone: PhoneNumberE164;
    entityId: ActivityParentEntityId;
}> = ({ phone, entityId }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryButton
                className={`items-center ${responsiveWidth}`}
                onClick={() => setIsOpen(true)}>
                <PhoneArrowUpRightIcon className={iconClass} />
                Call
            </SecondaryButton>
            <Panel
                isOpen={isOpen}
                onDismiss={() => setIsOpen(false)}
                type={PanelType.medium}
                headerText="Phone Call"
                hasCloseButton={true}>
                <PhoneCall phoneNumber={phone} entityId={entityId} />
            </Panel>
        </>
    );
};

export const AgendaTextAction: React.FC<{ phone: PhoneNumberE164 }> = ({ phone }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryButton
                className={`items-center ${responsiveWidth}`}
                onClick={() => setIsOpen(true)}>
                <ChatBubbleLeftRightIcon className={iconClass} />
                Text
            </SecondaryButton>
            <SendSmsPanel
                isOpen={isOpen}
                onDismiss={() => setIsOpen(false)}
                recipient={phone}
            />
        </>
    );
};

export const AgendaProvisionBanner: React.FC<unknown> = () => (
    <InlineBanner type="info">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
                Set up a phone number to call and text your referral sources right from My
                Agenda.
            </span>
            <Link
                to={provisionPath}
                className={`items-center whitespace-nowrap ${responsiveWidth} ${primaryClasses}`}>
                <PhoneArrowUpRightIcon className={iconClass} />
                Set up phone number
            </Link>
        </div>
    </InlineBanner>
);
